import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  VerticalAlignBottomOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
} from "antd";
import Title from "antd/es/typography/Title";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import MomentDatePicker from "../../Components/MomentDatePicker";
import {
  createRefueling,
  deleteRefueling,
  exportData,
  exportRefueling,
  getAllOil,
  getAllRefueling,
  getGasStation,
  getRemainingFuel,
  lockRefueling,
  updateRefueling,
} from "../../shared/api/Oil";
import API from "../../shared/api/api";
import { dateFormat } from "../../shared/dataFormat";
import { useRequestLock } from "../../shared/hooks/useRequestLock";
import { rule_number, rule_require } from "../../shared/validateInput";
import { UserAtom } from "../../store";
import { checkPermissions } from "../../utils/checkPermissions";
import "./refuelOil.css";

const { RangePicker } = MomentDatePicker;

const formLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
};

export const RefuelOilTab = () => {
  const permissionState = useRecoilValue<any>(UserAtom.permissions);
  const features = permissionState?.features;
  const canAddRefuel = checkPermissions("FUEL", "add", features);
  const canEditRefuel = checkPermissions("FUEL", "edit", features);
  const canDeleteRefuel = checkPermissions("FUEL", "delete", features);
  const fuelPermissionsRef = useRef({
    add: canAddRefuel,
    edit: canEditRefuel,
    delete: canDeleteRefuel,
  });
  fuelPermissionsRef.current = {
    add: canAddRefuel,
    edit: canEditRefuel,
    delete: canDeleteRefuel,
  };
  const [form] = Form.useForm();
  const [formExport] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [openExportModal, setOpenExportModal] = useState(false);
  const [openLockModal, setOpenLockModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string>();
  const [lockDate, setLockDate] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>();
  const { isLocked, runLocked } = useRequestLock();
  const refuelSaveKey = `refuel:${selectedItem?._id ?? "create"}`;
  const isSavingRefuel = isLocked(refuelSaveKey);
  const [refuelOil, setRefuelOil] = useState({
    list: [],
    loading: false,
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
  });
  const [remainingFuel, setRemainingFuel] = useState({
    list: [],
    loading: false,
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
  });
  const [latestOilPrice, setLatestOilPrice] = useState(0);
  const [listGasStations, setListGasStations] = useState([]);
  const [listDriver, setListDriver] = useState([]);
  const [listVehicle, setListVehicle] = useState([]);

  const closeRefuelModal = () => {
    setOpen(false);
    setSelectedItem(undefined);
    form.resetFields();
  };

  const fetchDataRefueling = async () => {
    setRefuelOil((prev) => ({ ...prev, loading: true }));
    const response: any = await getAllRefueling({
      params: {
        current: refuelOil.pagination.current,
        pageSize: refuelOil.pagination.pageSize,
      },
    });
    if (response.success) {
      setRefuelOil((prev) => ({
        ...prev,
        list: response?.data ?? [],
        loading: false,
        pagination: {
          ...prev.pagination,
          total: response.totalItem ?? 0,
        },
      }));
    } else {
      setRefuelOil((prev) => ({ ...prev, loading: false }));
    }
  };

  const fetchDataGasStation = async () => {
    const response: any = await getGasStation({});
    setListGasStations(response.data ?? []);
  };

  const fetchLatestOilPrice = async () => {
    const response: any = await getAllOil({
      params: {
        current: 1,
        pageSize: 1,
      },
    });
    setLatestOilPrice(response?.data?.[0]?.price ?? 0);
  };

  const fetchDataRemainingFuel = async () => {
    setRemainingFuel((prev) => ({ ...prev, loading: true }));
    const response: any = await getRemainingFuel({
      current: remainingFuel.pagination.current,
      pageSize: remainingFuel.pagination.pageSize,
    });
    setRemainingFuel((prev) => ({
      ...prev,
      list: response?.data ?? [],
      loading: false,
      pagination: {
        ...prev.pagination,
        total: response.totalItem ?? 0,
      },
    }));
  };
  const getListDriver = async () => {
    const response = await API.get("admin/driver").then((res) => res.data);
    setListDriver(response.data ?? []);
  };

  const getListVehicle = async () => {
    const response = await API.get("admin/vehicle", {
      params: { ownership: "INVESTMENT" },
    }).then((res) => res.data);
    setListVehicle(response.data ?? []);
  };

  useEffect(() => {
    Promise.all([
      fetchDataGasStation(),
      fetchLatestOilPrice(),
      getListDriver(),
      getListVehicle(),
    ]);
  }, []);

  useEffect(() => {
    fetchDataRemainingFuel();
  }, [remainingFuel.pagination.current]);

  useEffect(() => {
    fetchDataRefueling();
  }, [refuelOil.pagination.current, refuelOil.pagination.pageSize]);

  useEffect(() => {
    const requiredAction = selectedItem ? "edit" : "add";
    if (open && !fuelPermissionsRef.current[requiredAction]) {
      closeRefuelModal();
    }
  }, [canAddRefuel, canEditRefuel, open, selectedItem]);

  useEffect(() => {
    if (!canDeleteRefuel) {
      setPendingDeleteId(undefined);
    }
  }, [canDeleteRefuel]);

  useEffect(() => {
    if (!canEditRefuel) {
      setOpenLockModal(false);
      setLockDate("");
    }
  }, [canEditRefuel]);

  const onSubmitForm = async () => {
    const requiredAction = selectedItem ? "edit" : "add";
    if (!fuelPermissionsRef.current[requiredAction]) return;

    await runLocked(refuelSaveKey, async () => {
      if (!fuelPermissionsRef.current[requiredAction]) return;
      const response = selectedItem
        ? await updateRefueling({ ...form.getFieldsValue() })
        : await createRefueling({ ...form.getFieldsValue() });

      if (!fuelPermissionsRef.current[requiredAction]) return;
      if (response.success) {
        await fetchDataRefueling();
        closeRefuelModal();
      }
    });
  };

  const handleCloseModal = () => {
    if (isLocked(refuelSaveKey)) return;
    closeRefuelModal();
  };

  const handleExport = async () => {
    const paramExport = formExport.getFieldsValue();
    await exportRefueling(paramExport);
    setOpenExportModal(false);
  };

  const handleLockRefueling = async () => {
    if (!fuelPermissionsRef.current.edit) return;
    await lockRefueling({ date: lockDate });
    if (!fuelPermissionsRef.current.edit) return;
    setOpenLockModal(false);
    setLockDate("");
    fetchDataRefueling();
  };

  const columnsRemaining = [
    {
      title: "STT",
      width: 50,
      render: (_id: any, _record: any, index: number) => index + 1,
    },
    {
      title: "Biển số",
      dataIndex: "vehicle",
    },
    {
      title: "Ngày đổ dầu gần nhất",
      dataIndex: "date",
      render: (value: any) => dateFormat(value),
    },
    {
      title: "Tiêu hao đến hiện tại",
      dataIndex: "consumption",
    },
    {
      title: "Dầu còn lại",
      dataIndex: "remaining",
    },
  ];

  const columns = [
    {
      title: "STT",
      width: 50,
      render: (_id: any, _record: any, index: number) => index + 1,
    },
    {
      title: "Biển số",
      dataIndex: ["vehicle", "licensePlate"],
    },
    {
      title: "Ngày đổ dầu",
      dataIndex: "date",
      render: (value: any) => dateFormat(value),
    },
    {
      title: "Số nhiên liệu đã dùng",
      dataIndex: ["refueling", "amount"],
    },
    {
      title: "Giá dầu",
      dataIndex: ["refueling", "gasPrice"],
    },
    {
      title: "Tác vụ",
      width: 100,
      render: (record: any) => (
        <Space size="middle">
          <Tooltip title="Sửa">
            <Button
              type="text"
              disabled={
                record.isLocked || !canEditRefuel
              }
              onClick={() => {
                if (!fuelPermissionsRef.current.edit) return;
                const data = {
                  _id: record._id,
                  date: moment(record.date),
                  vehicle: record.vehicle._id,
                  driver: record.driver._id,
                  refueling: {
                    station: record.refueling.station._id,
                    amount: record.refueling.amount,
                    gasPrice: record.refueling.gasPrice,
                  },
                };
                setOpen(true);
                setSelectedItem(data);
                form.setFieldsValue(data);
              }}
            >
              <EditOutlined style={{ fontSize: 16 }} />
            </Button>
          </Tooltip>
          <Popconfirm
            title="Bạn có muốn xóa?"
            open={pendingDeleteId === record._id}
            onOpenChange={(nextOpen) => {
              if (!nextOpen) {
                setPendingDeleteId(undefined);
                return;
              }
              if (record.isLocked || !fuelPermissionsRef.current.delete) return;
              setPendingDeleteId(record._id);
            }}
            onConfirm={async () => {
              if (!fuelPermissionsRef.current.delete) return;
              try {
                await deleteRefueling(record?._id);
                if (!fuelPermissionsRef.current.delete) return;
                await fetchDataRefueling();
              } finally {
                setPendingDeleteId(undefined);
              }
            }}
            onCancel={() => setPendingDeleteId(undefined)}
            okText="Có"
            cancelText="Hủy"
          >
            <Button
              type="text"
              disabled={record.isLocked || !canDeleteRefuel}
            >
              <DeleteOutlined
                style={{ fontSize: 16, color: "#FF4E4F", cursor: "pointer" }}
              />
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" gutter={6}>
        <Title level={4}>Quản lý đổ dầu</Title>
        <Space size={6}>
          <Button
            className="custom-button"
            onClick={() => {
              if (!fuelPermissionsRef.current.edit) return;
              setOpenLockModal(true);
            }}
            type="text"
            size="small"
            disabled={!canEditRefuel}
          >
            Chốt dầu
          </Button>
          {/* <Button
            onClick={() => {
              setOpenExportModal(true);
            }}
            type="default"
            size="small"
            disabled={!checkPermissions("FUEL", "export", features)}
          >
            Tải báo cáo
          </Button>
          <Button
            onClick={exportData}
            type="default"
            size="small"
            disabled={!checkPermissions("FUEL", "export", features)}
          >
            Export
          </Button> */}
          <Button
            onClick={() => {
              if (!fuelPermissionsRef.current.add) return;
              form.setFieldValue(["refueling", "gasPrice"], latestOilPrice);
              setOpen(true);
            }}
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            disabled={!canAddRefuel}
          >
            Tạo mới
          </Button>
        </Space>
      </Row>
      <br />
      <Row gutter={10}>
        <Col span={12}>
          <Table
            id="remaining"
            scroll={{ x: "max-content" }}
            rowKey="_id"
            loading={remainingFuel.loading}
            columns={columnsRemaining}
            pagination={
              remainingFuel.pagination.total >
                remainingFuel.pagination.pageSize && remainingFuel.pagination
            }
            dataSource={remainingFuel.list}
            onChange={(value: any) =>
              setRemainingFuel((prev) => ({
                ...prev,
                pagination: {
                  ...prev.pagination,
                  current: value.current,
                },
              }))
            }
          />
        </Col>
        <Col span={12}>
          <Table
            id="refuel"
            scroll={{ x: "max-content" }}
            rowKey="_id"
            loading={refuelOil.loading}
            columns={columns}
            pagination={
              refuelOil.pagination.total > refuelOil.pagination.pageSize &&
              refuelOil.pagination
            }
            dataSource={refuelOil.list}
            onChange={(value: any) =>
              setRefuelOil((prev) => ({
                ...prev,
                pagination: { ...prev.pagination, current: value.current },
              }))
            }
          />
        </Col>
      </Row>

      <Modal
        open={open}
        title={selectedItem ? "Cập nhật đổ dầu" : "Tạo đổ dầu"}
        confirmLoading={isSavingRefuel}
        closable={!isSavingRefuel}
        maskClosable={!isSavingRefuel}
        keyboard={!isSavingRefuel}
        onCancel={handleCloseModal}
        onOk={() => {
          if (isLocked(refuelSaveKey)) return;
          form.submit();
        }}
        okButtonProps={{
          disabled:
            isSavingRefuel ||
            !fuelPermissionsRef.current[selectedItem ? "edit" : "add"],
        }}
        cancelButtonProps={{ disabled: isSavingRefuel }}
      >
        <Form
          {...formLayout}
          form={form}
          onFinish={onSubmitForm}
          layout="vertical"
        >
          <Form.Item name="_id" label="id" hidden>
            <Input type="text" disabled hidden />
          </Form.Item>
          <Row style={{ padding: 10 }}>
            <Col span={12}>
              <Form.Item
                name="vehicle"
                label="Phương tiện"
                rules={[rule_require]}
              >
                <Select
                  showSearch
                  style={{ width: 200 }}
                  placeholder="Chọn phương tiện"
                  fieldNames={{ value: "_id", label: "licensePlate" }}
                  options={listVehicle}
                  onSelect={(_value, option: any) => {
                    form.setFieldValue("driver", option?.driver?._id);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="driver" label="Tài xế">
                <Select
                  disabled
                  showSearch
                  style={{ width: 200 }}
                  placeholder="Chọn tài xế"
                  fieldNames={{ value: "_id", label: "name" }}
                  options={listDriver}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="date" label="Ngày" rules={[rule_require]}>
                <MomentDatePicker
                  format="DD/MM/YYYY"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={["refueling", "station"]}
                label="Địa điểm"
                rules={[rule_require]}
              >
                <Select
                  showSearch
                  style={{ width: 200 }}
                  placeholder="Chọn cây xăng"
                  fieldNames={{ value: "_id", label: "name" }}
                  options={listGasStations}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={["refueling", "amount"]}
                label="Số lượng"
                rules={[rule_number, rule_require]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    ` ${value
                      ?.toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")} `
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={["refueling", "gasPrice"]}
                label="Giá dầu"
                rules={[rule_number, rule_require]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    ` ${value
                      ?.toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")} `
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        width={600}
        destroyOnClose={true}
        closable={true}
        title="Tải báo cáo"
        open={openExportModal}
        onCancel={() => setOpenExportModal(false)}
        onOk={() => {
          formExport.submit();
        }}
        okText={
          <>
            <VerticalAlignBottomOutlined />
            Tải xuống
          </>
        }
        cancelText="Hủy"
      >
        <Form
          {...formLayout}
          form={formExport}
          onFinish={() => {
            handleExport();
          }}
        >
          <Form.Item
            label="Thời gian"
            name="range"
            initialValue={[moment().startOf("month"), moment().endOf("month")]}
          >
            <RangePicker
              name="range"
              style={{ width: 250 }}
              ranges={{
                "Tháng này": [
                  moment().startOf("month"),
                  moment().endOf("month"),
                ],
                "Tháng trước": [
                  moment().subtract(1, "month").startOf("month"),
                  moment().subtract(1, "month").endOf("month"),
                ],
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        width={300}
        destroyOnClose={true}
        closable={true}
        title="Chọn ngày chốt dầu"
        open={openLockModal}
        onCancel={() => setOpenLockModal(false)}
        onOk={handleLockRefueling}
        okText={
          <>
            <VerticalAlignBottomOutlined />
            Xác nhận
          </>
        }
        cancelText="Hủy"
        footer={
          <Space>
            <Button onClick={() => setOpenLockModal(false)}>Huỷ</Button>
            <Popconfirm
              title={`Bạn có chắc muốn chốt dầu vào ngày ${lockDate}?`}
              onConfirm={handleLockRefueling}
            >
              <Button
                type="primary"
                disabled={
                  !lockDate || !canEditRefuel
                }
              >
                Xác nhận
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <MomentDatePicker
          format="YYYY-MM-DD"
          style={{ width: "100%" }}
          onChange={(date) => setLockDate(moment(date).format("YYYY-MM-DD"))}
        />
      </Modal>
    </>
  );
};
